import React from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

let nextTempId = -1;

export const newEmptyStep = () => ({
  id: nextTempId--,
  name: "",
  description: "",
});

/**
 * Shared vertical Stepper used both to author an OperationProcess's steps
 * (mode="edit") and to render/select a CPO's progress through one
 * (mode="select" allows clicking a step, mode="view" is read-only).
 */
export default function OperationProcessStepper({
  mode = "view",
  steps,
  onChange,
  activeStepId,
  onSelectStep,
}) {
  const handleFieldChange = (index, field, value) => {
    const next = steps.slice();
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const handleRemove = (index) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const handleMove = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= steps.length) return;
    const next = steps.slice();
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...steps, newEmptyStep()]);
  };

  if (mode === "edit") {
    return (
      <Box>
        <Stepper orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step key={step.id} active>
              <StepLabel>
                <TextField
                  size="small"
                  label={`Paso ${index + 1}`}
                  value={step.name}
                  onChange={(e) => handleFieldChange(index, "name", e.target.value)}
                  fullWidth
                />
              </StepLabel>
              <StepContent TransitionProps={{ in: true }}>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Descripción"
                    value={step.description}
                    onChange={(e) =>
                      handleFieldChange(index, "description", e.target.value)
                    }
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                      aria-label="Mover arriba"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === steps.length - 1}
                      onClick={() => handleMove(index, 1)}
                      aria-label="Mover abajo"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(index)}
                      aria-label="Eliminar paso"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        <Button startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
          Agregar paso
        </Button>
      </Box>
    );
  }

  const activeIndex = steps.findIndex((s) => s.id === activeStepId);

  return (
    <Stepper orientation="vertical" activeStep={activeIndex} nonLinear={mode === "select"}>
      {steps.map((step, index) => (
        <Step key={step.id} completed={activeIndex >= 0 && index < activeIndex}>
          {mode === "select" ? (
            <StepButton onClick={() => onSelectStep?.(step.id)}>
              {step.name}
            </StepButton>
          ) : (
            <StepLabel>{step.name}</StepLabel>
          )}
          {step.description ? (
            <StepContent TransitionProps={{ in: true }}>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepContent>
          ) : null}
        </Step>
      ))}
    </Stepper>
  );
}
